<template>
  <span class="tokeva-rich-text">
    <template v-for="(fragment, idx) in fragments">
      <!-- Leading step number (e.g. "1.", "14.") -->
      <strong
        v-if="fragment.type === 'number'"
        :key="idx"
        class="tokeva-step-num mr-1"
      >{{ fragment.text }}</strong>

      <!-- Regular text -->
      <span
        v-else-if="fragment.type === 'text'"
        :key="idx"
        class="tokeva-text"
      >{{ fragment.text }}</span>

      <!-- Italic text -->
      <em
        v-else-if="fragment.type === 'italic'"
        :key="idx"
        class="tokeva-italic font-italic"
      >{{ fragment.text }}</em>

      <!-- M-ohje link pill (Official PDF / Tokeva M-guide) -->
      <a
        v-else-if="fragment.type === 'link' && fragment.isM"
        :key="idx"
        :href="fragment.url"
        target="_blank"
        rel="noopener noreferrer"
        class="tokeva-m-pill"
        :title="`Avaa ${fragment.text} virallinen ohje (PDF)`"
      >
        <span class="tokeva-pill-text">{{ fragment.text }}</span>
        <v-icon x-small class="ml-1 tokeva-pill-icon">mdi-open-in-new</v-icon>
      </a>

      <!-- CBRNE link pill -->
      <a
        v-else-if="fragment.type === 'link' && fragment.isCbrne"
        :key="idx"
        :href="fragment.url"
        target="_blank"
        rel="noopener noreferrer"
        class="tokeva-cbrne-pill"
        :title="`Avaa ${fragment.text} virallinen ohje (PDF)`"
      >
        <v-icon x-small class="mr-1">mdi-biohazard</v-icon>
        <span class="tokeva-pill-text">{{ fragment.text }}</span>
        <v-icon x-small class="ml-1 tokeva-pill-icon">mdi-open-in-new</v-icon>
      </a>

      <!-- Neutralointilaskuri action button pill -->
      <a
        v-else-if="fragment.type === 'link' && fragment.isCalc"
        :key="idx"
        :href="fragment.url"
        target="_blank"
        rel="noopener noreferrer"
        class="tokeva-calc-pill"
        title="Avaa virallinen Neutralointilaskuri (Tokeva.fi)"
      >
        <v-icon x-small class="mr-1">mdi-calculator</v-icon>
        <span class="tokeva-pill-text font-weight-bold">{{ fragment.text }}</span>
        <v-icon x-small class="ml-1 tokeva-pill-icon">mdi-open-in-new</v-icon>
      </a>

      <!-- General link (STUK, SÖKÖ, ICSC, OVA, etc.) -->
      <a
        v-else-if="fragment.type === 'link'"
        :key="idx"
        :href="fragment.url"
        target="_blank"
        rel="noopener noreferrer"
        class="tokeva-general-link"
        :title="fragment.url"
      >
        <span>{{ fragment.text }}</span>
        <v-icon x-small class="ml-1">mdi-open-in-new</v-icon>
      </a>

      <!-- Gas cylinder color guide link -->
      <v-chip
        v-else-if="fragment.type === 'gas-info'"
        :key="idx"
        x-small
        outlined
        color="primary"
        class="mx-1 font-weight-medium gas-chip"
        @click="$emit('open-gas-guide')"
      >
        <v-icon left x-small>mdi-gas-cylinder</v-icon>
        {{ fragment.text }}
      </v-chip>
    </template>
  </span>
</template>

<script>
export default {
  name: "TokevaRichText",
  props: {
    content: {
      type: [String, Object, Array],
      default: "",
    },
    formatNumber: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    fragments() {
      const rawFragments = this.parseFragments(this.content);
      if (!this.formatNumber || rawFragments.length === 0) {
        return rawFragments;
      }

      // If formatNumber is true, inspect the first text fragment to bold leading "1.", "14.", etc.
      const firstIdx = rawFragments.findIndex((f) => f.type === "text" && f.text);
      if (firstIdx === -1) return rawFragments;

      const firstFrag = rawFragments[firstIdx];
      const match = firstFrag.text.match(/^(\s*\d+\.\s*)(.*)$/s);
      if (!match) return rawFragments;

      const result = [...rawFragments];
      const leadingNumber = match[1];
      const restText = match[2];

      const replacement = [{ type: "number", text: leadingNumber }];
      if (restText) {
        replacement.push({ type: "text", text: restText });
      }

      result.splice(firstIdx, 1, ...replacement);
      return result;
    },
  },
  methods: {
    resolveLink(target) {
      if (!target) return { text: "", url: "" };

      // Case 1: string tag (e.g. 'cbrne', 'm1a', 'm10c', 't1')
      if (typeof target === "string") {
        const tag = target.trim().toLowerCase();
        if (tag === "cbrne") {
          return {
            text: "Ohje CBRNE",
            url: "https://tokeva.fi/assets/attachment-files/TOhjeet/Tokeva_Cbrne-ohje.pdf",
            webUrl: "https://tokeva.fi/#/t-ohjeet/tohje/cbrne",
            isCbrne: true,
            isPdf: true,
          };
        }
        if (tag.startsWith("m")) {
          const cap = tag.charAt(0).toUpperCase() + tag.slice(1);
          return {
            text: `Ohje ${cap}`,
            url: `https://tokeva.fi/assets/attachment-files/MOhjeet/Tokeva_${cap}-ohje.pdf`,
            webUrl: `https://tokeva.fi/#/m-ohjeet/${tag}`,
            isM: true,
            isPdf: true,
          };
        }
        if (tag.startsWith("t")) {
          const cap = tag.charAt(0).toUpperCase() + tag.slice(1);
          return {
            text: `Ohje ${cap}`,
            url: `https://tokeva.fi/assets/attachment-files/TOhjeet/Tokeva_${cap}-ohje.pdf`,
            webUrl: `https://tokeva.fi/#/t-ohjeet/tohje/${tag}`,
            isT: true,
            isPdf: true,
          };
        }
        return {
          text: target,
          url: `https://tokeva.fi/#/${target}`,
        };
      }

      // Case 2: Object with linkkiTag
      if (target.linkkiTag || target.linkkitag) {
        return this.resolveLink(target.linkkiTag || target.linkkitag);
      }

      // Case 3: Object with osoite / linkkiTeksti
      const rawUrl = target.osoite || "";
      const rawText = target.linkkiTeksti || target.teksti || rawUrl;

      if (rawUrl.includes("neutralointilaskuri")) {
        return {
          text: rawText || "Neutralointilaskuri",
          url: "https://tokeva.fi/#/neutralointilaskuri",
          isCalc: true,
        };
      }
      if (rawUrl.startsWith("#/m-ohjeet/")) {
        const code = rawUrl.replace("#/m-ohjeet/", "").trim();
        const cap = code.charAt(0).toUpperCase() + code.slice(1);
        return {
          text: rawText || `Ohje ${cap}`,
          url: `https://tokeva.fi/assets/attachment-files/MOhjeet/Tokeva_${cap}-ohje.pdf`,
          webUrl: `https://tokeva.fi/${rawUrl}`,
          isM: true,
          isPdf: true,
        };
      }
      if (rawUrl.startsWith("#/t-ohjeet/tohje/")) {
        return {
          text: rawText || "T-ohje",
          url: `https://tokeva.fi/${rawUrl}`,
          isT: true,
        };
      }
      if (rawUrl.startsWith("assets/") || rawUrl.startsWith("/assets/")) {
        return {
          text: rawText,
          url: `https://tokeva.fi/${rawUrl.replace(/^\//, "")}`,
          isPdf: rawUrl.endsWith(".pdf"),
        };
      }
      if (rawUrl.startsWith("http://") || rawUrl.startsWith("https://")) {
        return {
          text: rawText,
          url: rawUrl,
          isExternal: true,
        };
      }

      return {
        text: rawText,
        url: rawUrl.startsWith("#") ? `https://tokeva.fi/${rawUrl}` : rawUrl,
      };
    },
    parseFragments(item) {
      if (item === null || item === undefined) return [];
      if (typeof item === "string") {
        return this.parseStringWithUrls(item);
      }
      if (Array.isArray(item)) {
        const result = [];
        for (const sub of item) {
          result.push(...this.parseFragments(sub));
        }
        return result;
      }
      if (item.linkkiTeksti || item.osoite) {
        return [{ type: "link", ...this.resolveLink(item) }];
      }
      if (item.linkkiTag || item.linkkitag) {
        return [{ type: "link", ...this.resolveLink(item.linkkiTag || item.linkkitag) }];
      }
      if (item.tyyliteksti || item.tyyliTeksti) {
        const txt = item.tyyliteksti || item.tyyliTeksti;
        const isItalic = (item.tyylit || []).includes("in-italic");
        return [{ type: isItalic ? "italic" : "text", text: txt }];
      }
      if (Array.isArray(item.kohtaSisalto)) {
        const fragments = [];
        for (const part of item.kohtaSisalto) {
          if (typeof part === "string") {
            fragments.push(...this.parseStringWithUrls(part));
          } else if (part && typeof part === "object") {
            if (part.linkkiTag || part.linkkitag) {
              fragments.push({
                type: "link",
                ...this.resolveLink(part.linkkiTag || part.linkkitag),
              });
            } else if (part.linkkiTeksti || part.osoite) {
              fragments.push({
                type: "link",
                ...this.resolveLink(part),
              });
            } else if (part.tyyliteksti || part.tyyliTeksti) {
              const isItalic = (part.tyylit || []).includes("in-italic");
              fragments.push({
                type: isItalic ? "italic" : "text",
                text: part.tyyliteksti || part.tyyliTeksti,
              });
            } else if (part.kaasupulloinfo) {
              fragments.push({
                type: "gas-info",
                text: "Kaasupullon kauluksen väri",
              });
            } else if (part.teksti) {
              fragments.push(...this.parseStringWithUrls(part.teksti));
            } else if (Array.isArray(part.kohtaSisalto)) {
              fragments.push(...this.parseFragments(part));
            }
          }
        }
        return fragments;
      }
      if (item.teksti) {
        return this.parseStringWithUrls(item.teksti);
      }
      return [];
    },
    parseStringWithUrls(str) {
      if (!str) return [];
      const urlRegex = /(https?:\/\/[^\s]+)/g;
      const parts = str.split(urlRegex);
      if (parts.length === 1) {
        return [{ type: "text", text: str }];
      }
      const fragments = [];
      for (const part of parts) {
        if (!part) continue;
        if (/^https?:\/\//i.test(part)) {
          fragments.push({
            type: "link",
            text: part,
            url: part,
            isExternal: true,
          });
        } else {
          fragments.push({ type: "text", text: part });
        }
      }
      return fragments;
    },
  },
};
</script>

<style scoped lang="scss">
.tokeva-rich-text {
  display: inline;
}

.tokeva-text {
  display: inline;
}

.tokeva-step-num {
  font-weight: 800;
  color: #0d47a1;
  font-size: 0.95rem;
}
.theme--dark .tokeva-step-num {
  color: #90caf9;
}

/* M-Ohje pill badge */
.tokeva-m-pill {
  display: inline-flex;
  align-items: center;
  background-color: #e3f2fd;
  color: #1565c0 !important;
  border: 1px solid #90caf9;
  border-radius: 4px;
  padding: 1px 7px;
  margin: 0 4px;
  font-size: 0.85rem;
  font-weight: 600;
  text-decoration: none;
  vertical-align: baseline;
  white-space: nowrap;
  transition: all 0.15s ease-in-out;
  cursor: pointer;

  &:hover {
    background-color: #bbdefb;
    border-color: #1976d2;
    text-decoration: none;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(25, 118, 210, 0.2);
  }
}
.theme--dark .tokeva-m-pill {
  background-color: #0f2742;
  color: #90caf9 !important;
  border: 1px solid #1e4976;

  &:hover {
    background-color: #183e66;
    border-color: #42a5f5;
  }
}

/* CBRNE pill badge */
.tokeva-cbrne-pill {
  display: inline-flex;
  align-items: center;
  background-color: #ffebee;
  color: #c62828 !important;
  border: 1px solid #ef9a9a;
  border-radius: 4px;
  padding: 1px 7px;
  margin: 0 4px;
  font-size: 0.85rem;
  font-weight: 700;
  text-decoration: none;
  vertical-align: baseline;
  white-space: nowrap;
  transition: all 0.15s ease-in-out;
  cursor: pointer;

  &:hover {
    background-color: #ffcdd2;
    border-color: #d32f2f;
    text-decoration: none;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(198, 40, 40, 0.25);
  }
}
.theme--dark .tokeva-cbrne-pill {
  background-color: #3b1212;
  color: #ef9a9a !important;
  border: 1px solid #8e2b2b;

  &:hover {
    background-color: #551c1c;
    border-color: #ef5350;
  }
}

/* Calculator pill button */
.tokeva-calc-pill {
  display: inline-flex;
  align-items: center;
  background-color: #e8f5e9;
  color: #2e7d32 !important;
  border: 1px solid #81c784;
  border-radius: 4px;
  padding: 2px 8px;
  margin: 0 4px;
  font-size: 0.85rem;
  font-weight: 700;
  text-decoration: none;
  vertical-align: baseline;
  white-space: nowrap;
  transition: all 0.15s ease-in-out;
  cursor: pointer;

  &:hover {
    background-color: #c8e6c9;
    border-color: #388e3c;
    text-decoration: none;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(46, 125, 50, 0.2);
  }
}
.theme--dark .tokeva-calc-pill {
  background-color: #103319;
  color: #a5d6a7 !important;
  border: 1px solid #2e7d32;

  &:hover {
    background-color: #1a4d26;
    border-color: #66bb6a;
  }
}

/* General Link */
.tokeva-general-link {
  display: inline-flex;
  align-items: center;
  color: #1976d2 !important;
  font-weight: 600;
  text-decoration: underline;
  margin: 0 3px;
  white-space: nowrap;

  &:hover {
    color: #0d47a1 !important;
  }
}
.theme--dark .tokeva-general-link {
  color: #64b5f6 !important;

  &:hover {
    color: #90caf9 !important;
  }
}

.gas-chip {
  cursor: pointer;
  transition: transform 0.15s ease;
  &:hover {
    transform: translateY(-1px);
  }
}
</style>

